import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DraggableLocation } from 'react-beautiful-dnd';

import reOrderSpreadsOperation from 'editor/src/store/design/operation/spreadManipulation/reoderSpreadsOperation';
import getSpreadGroups from 'editor/src/store/design/selector/getSpreadGroups';
import setCurrentSpreadIndexOperation from 'editor/src/store/editor/operation/setCurrentSpreadIndexOperation';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import { getDesignKeyFromDesign } from 'editor/src/store/variants/helpers/getDesignKey';

import retryPromiseFn from 'editor/src/util/retryPromiseFn';

import { onPageSelectorUpdateFn, RIGHT_CONTROL_BOTTOM_OFFSET } from 'editor/src/component/EditorArea/types';
import { extractDisplayPages } from 'editor/src/component/EditorArea/utils/displayPageUtils';

import { THUMBNAILS_HEIGHT_MOBILE, useBottomBarHeight } from './BottomBarHeightProvider';
import { PageSelectorRef } from './PageSelector';

const PageSelector = React.lazy(
  retryPromiseFn(() => import('editor/src/component/EditorArea/EditorAreaControls/BottomControls/PageSelector')),
);
const BottomRightControls = React.lazy(
  retryPromiseFn(() => import('editor/src/component/EditorArea/EditorAreaControls/BottomControls/BottomRightControls')),
);

interface Props {
  currentSpreadIndex: number;
  isMobile: boolean;
  onPageSelectorUpdate?: onPageSelectorUpdateFn;
}

const RIGHT_CTRL_MARGIN = 40;
export const BOTTOM_BAR_WIDTH = 150;

function BottomControls({ currentSpreadIndex, isMobile, onPageSelectorUpdate }: Props) {
  const store = useStore();
  const dispatch = useDispatch();
  const [rightControlsBottomOffset, setRightControlsBottomOffset] = useState(RIGHT_CONTROL_BOTTOM_OFFSET);

  const spreads = store.getState().design.designData?.spreads ?? [];
  const pageSelectorRef = useRef<PageSelectorRef>(null);
  const pageSelectorSize = useRef({ height: 0, bottomOffset: 0 });
  const { updateBottomBarHeight } = useBottomBarHeight();

  const designKey = useSelector((state) =>
    state.design.designData ? getDesignKeyFromDesign(state.design.designData) : '',
  );
  const spreadGroups = useSelector(getSpreadGroups);

  const splitDisplayPerContent = useSelector((state) => getHostSetting(state, 'splitDisplayPerContent'));
  // GAPI-30848: when multi-tech mode is on and we have area groups, filter
  // the display pages to one per canonical area, using the active tech's
  // spread for each.
  const multiTechAreaGroups = useSelector((state) => state.app.multiTechAreaGroups);
  const selectedTechByArea = useSelector((state) => state.app.selectedTechByArea);
  // GAPI-30848 (Bug 2 — Tech toggle): when the top-level GarmentTechnology
  // control is set to Embroidery, the design template returned for the
  // embroidery variant still ships the full product's spreads (e.g. Bella's
  // back-dtg / front-dtg / chest-center-embroidery / sleeve-top-left-dtg /
  // sleeve-top-right-dtg). The strip should display only the embroidery
  // areas in this mode — print-area tiles belong to the Printing mode.
  const selectedTechnology = useSelector(
    (state) => (state.variants.selectedSingleOptions as Record<string, string>)?.GarmentTechnology ?? '',
  );
  const isEmbroideryMode = selectedTechnology.toLowerCase() === 'embroidery';
  const displayPages = useMemo(() => {
    const spreadsArr = store.getState().design.designData?.spreads ?? [];
    // GAPI-30848: in multi-tech mode, skip the `spread_groups` filter inside
    // extractDisplayPages — the host may have injected synthetic placeholder
    // spreads (one per canonical-area × tech) that aren't referenced by any
    // pre-existing spread group, and we still want them in the strip so the
    // per-area picker can switch onto them.
    const isMultiTech = !!multiTechAreaGroups?.length && !!selectedTechByArea;
    const all = extractDisplayPages(spreadsArr, splitDisplayPerContent, isMultiTech ? undefined : spreadGroups);
    // GAPI-30848 (Bug 2 — Tech toggle): filter the strip by GarmentTechnology.
    //   - Embroidery: keep only spreads ending in `-embroidery` / `-emb`
    //     (chest-center for Bella). Mirrors the catalogue PDP behaviour
    //     where Embroidery is a top-level mode that swaps print areas for
    //     embroidery areas.
    //   - Printing (any non-embroidery selection): drop embroidery spreads
    //     so they don't leak alongside the print tiles when the variant's
    //     template happens to include both (Bella DTF template ships with
    //     chest-center-embroidery too).
    const isEmbroiderySpreadName = (name?: string) => {
      const lc = name?.toLowerCase() ?? '';
      return lc.endsWith('-embroidery') || lc.endsWith('-emb');
    };
    if (isEmbroideryMode) {
      const embroideryFiltered = all.filter((dp) => isEmbroiderySpreadName(spreadsArr[dp.spreadIndex]?.name));
      return embroideryFiltered.length > 0 ? embroideryFiltered : all;
    }
    if (!isMultiTech) {
      return all.filter((dp) => !isEmbroiderySpreadName(spreadsArr[dp.spreadIndex]?.name));
    }
    // Stable order: walk areaGroups in their declared order and pick the
    // active tech's spread for each. Switching tech for one area does not
    // shuffle the others.
    const dpByName = new Map<string, (typeof all)[number]>();
    all.forEach((dp) => {
      const name = spreadsArr[dp.spreadIndex]?.name;
      if (name) {
        dpByName.set(name, dp);
      }
    });
    const ordered: typeof all = [];
    multiTechAreaGroups.forEach((group) => {
      const activeTech = selectedTechByArea[group.canonicalName] ?? group.options[0]?.technology;
      const match = group.options.find((opt) => opt.technology === activeTech) ?? group.options[0];
      const dp = match?.spreadName ? dpByName.get(match.spreadName) : undefined;
      if (dp) {
        ordered.push(dp);
      }
    });
    return ordered.length > 0 ? ordered : all;
  }, [
    designKey,
    splitDisplayPerContent,
    spreadGroups,
    spreads.length,
    multiTechAreaGroups,
    selectedTechByArea,
    isEmbroideryMode,
  ]);

  const onPageWidthUpdate = useCallback(
    (availableSpace: number) => {
      let newRightControlsBottomOffset = RIGHT_CONTROL_BOTTOM_OFFSET;
      const pageSelectorFullHeight = pageSelectorSize.current.height + pageSelectorSize.current.bottomOffset;
      if (availableSpace < BOTTOM_BAR_WIDTH + RIGHT_CTRL_MARGIN && pageSelectorSize.current.height > 0) {
        const offsetFromSelectorToRightControls = 17;
        newRightControlsBottomOffset = pageSelectorFullHeight + offsetFromSelectorToRightControls;
      }

      updateBottomBarHeight(isMobile ? THUMBNAILS_HEIGHT_MOBILE : pageSelectorFullHeight);
      setRightControlsBottomOffset(newRightControlsBottomOffset);
    },
    [updateBottomBarHeight],
  );

  const showPageSelector = displayPages.length > 1;

  // GAPI-30848 (Bug 2 — Tech toggle): when the customer flips to Embroidery,
  // jump the canvas to the (first) embroidery spread so they land on
  // chest-center-embroidery instead of staying on whatever print spread was
  // active (e.g. back-dtg). The filter above already shrinks displayPages to
  // embroidery-only — we just snap currentSpreadIndex to its first entry.
  useEffect(() => {
    if (!isEmbroideryMode || displayPages.length === 0) {
      return;
    }
    const targetIndex = displayPages[0]?.spreadIndex;
    if (typeof targetIndex === 'number' && targetIndex !== currentSpreadIndex) {
      dispatch(setCurrentSpreadIndexOperation(targetIndex));
    }
  }, [isEmbroideryMode, displayPages, currentSpreadIndex, dispatch]);

  const pageSelectorUpdated: onPageSelectorUpdateFn = (bottomOffset: number, height: number) => {
    if (pageSelectorSize.current.bottomOffset === bottomOffset && pageSelectorSize.current.height === height) {
      return;
    }

    pageSelectorSize.current = { height, bottomOffset };
    onPageSelectorUpdate?.(bottomOffset, height);
  };

  useEffect(() => {
    if (!showPageSelector) {
      onPageSelectorUpdate?.(0, 0);
    }
  }, [showPageSelector]);

  const handleOrderChange = useCallback((source: DraggableLocation, destination?: DraggableLocation | null) => {
    // dropped outside the list
    if (!destination) {
      return;
    }

    const spreads = store.getState().design.designData?.spreads ?? [];

    const destinationSpreadIndex = displayPages[destination.index - 1]?.spreadIndex;
    const isDestinationStart = destination.index === 0;
    const isDestinationEnd = destination.index === displayPages.length - 1;

    const canAddAfter = destinationSpreadIndex && spreads[destinationSpreadIndex].canAddAfter;

    if (isDestinationStart || isDestinationEnd || !canAddAfter) {
      return;
    }

    dispatch(reOrderSpreadsOperation(source.index, destination.index, displayPages));
  }, []);

  return (
    <Suspense fallback="">
      {showPageSelector && (
        <PageSelector
          ref={pageSelectorRef}
          currentSpreadIndex={currentSpreadIndex}
          isMobile={isMobile}
          displayPages={displayPages}
          onPageWidthUpdate={onPageWidthUpdate}
          onPageSelectorUpdate={pageSelectorUpdated}
          onOrderChange={handleOrderChange}
        />
      )}
      {!isMobile && <BottomRightControls bottomOffset={rightControlsBottomOffset} />}
    </Suspense>
  );
}

export default React.memo(BottomControls);
